import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { EditableInput } from '../EditableInput';
import styles from './editableTextArea.module.scss';

export const EditableTextArea = ({
  label,
  value,
  disabled,
  editable,
  placeholder,
  maxLength,
  width,
  className,
  focusFirstTime,
  editOnly,
  onChange,
  onSwitch,
}) => {
  const editRef = useRef(null);
  const textRef = useRef(null);
  const focusFirstTimeRef = useRef(focusFirstTime);
  const [numOfChars, setNumOfChars] = useState(value == null ? 0 : value.length);

  useEffect(() => {
    if (focusFirstTimeRef.current) {
      textRef.current?.focus();
    }
  }, []);

  const handleChange = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      if (maxLength != null) {
        setNumOfChars(e.target.value.length);
      }
      if (onChange) {
        onChange(e);
      }
    },
    [disabled, maxLength, onChange],
  );

  const adjustText = useCallback(
    (e) => {
      if (maxLength != null && textRef.current) {
        let text = textRef.current.value;
        if (text?.length > maxLength) {
          text = text.slice(0, maxLength);
          textRef.current.value = text;
          e.target.value = text;
          setNumOfChars(text.length);
        }
        onChange?.(e);
      }
    },
    [maxLength, onChange],
  );

  const handleBlur = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      adjustText(e);
      if (onSwitch) {
        onSwitch(e);
      }
      if (!editOnly) {
        setTimeout(() => editRef.current?.focus());
      }
    },
    [disabled, editOnly, onSwitch, adjustText],
  );

  const handleClick = useCallback(
    (e) => {
      if (!disabled) {
        adjustText(e);
      }
    },
    [disabled, adjustText],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      if (!editOnly) {
        if (e.key === 'Tab' || e.key === 'Escape') {
          e.preventDefault();
          textRef.current?.blur();
        }
      }
    },
    [disabled, editOnly, textRef],
  );

  return (
    <EditableInput
      ref={editRef}
      inputRef={textRef}
      label={label}
      value={value}
      disabled={disabled}
      editable={editable}
      editOnly={editOnly}
      width={width}
      className={className}
      textClassName={styles.text}
      onClick={onSwitch}
      multiLine
    >
      <div className={styles.container}>
        <textarea
          ref={textRef}
          value={value}
          disabled={disabled}
          style={{ width }}
          className={classNames(styles.input, styles.text)}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
        />
        {maxLength != null && <div className={styles.count}>{`${numOfChars}/${maxLength}文字`}</div>}
      </div>
    </EditableInput>
  );
};

EditableTextArea.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  editable: PropTypes.bool,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  focusFirstTime: PropTypes.bool,
  editOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onSwitch: PropTypes.func,
};

EditableTextArea.defaultProps = {
  label: undefined,
  value: undefined,
  disabled: false,
  editable: undefined,
  placeholder: undefined,
  maxLength: undefined,
  width: 'auto',
  className: undefined,
  focusFirstTime: false,
  editOnly: false,
  onChange: undefined,
  onSwitch: undefined,
};
